import React, { useEffect, useRef } from 'react';

interface Plane {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  speed: number;
  trail: { x: number; y: number }[];
  trailLength: number;
  opacity: number;
  path: { x: number; y: number }[];
  pathProgress: number;
}

const PaperPlanes: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let planes: Plane[] = [];
    const planeCount = 3;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const generatePath = () => {
      const path: { x: number; y: number }[] = [];
      let x = -50;
      let y = Math.random() * canvas.height;
      
      path.push({ x, y });

      const points = Math.floor(Math.random() * 3) + 3;
      for (let i = 0; i < points; i++) {
        x += canvas.width / (points - 1) + Math.random() * 100 - 50;
        y = Math.random() * canvas.height;
        path.push({ x, y });
      }

      path.push({ x: canvas.width + 50, y: Math.random() * canvas.height });
      return path;
    };

    const createPlane = (): Plane => ({
      x: -50,
      y: Math.random() * canvas.height,
      rotation: 0,
      scale: Math.random() * 0.3 + 0.7,
      speed: Math.random() * 0.005 + 0.002,
      trail: [],
      trailLength: Math.floor(Math.random() * 10) + 15,
      opacity: Math.random() * 0.3 + 0.7,
      path: generatePath(),
      pathProgress: 0
    });

    const initPlanes = () => {
      planes = Array.from({ length: planeCount }, () => createPlane());
      // Stagger initial positions
      planes.forEach((plane, i) => {
        plane.pathProgress = i * (1 / planeCount);
      });
    };

    const getBezierPoint = (points: { x: number; y: number }[], t: number) => {
      const p0 = points[0];
      const p1 = points[1];
      const p2 = points[2];
      const p3 = points[3];

      const cx = 3 * (p1.x - p0.x);
      const bx = 3 * (p2.x - p1.x) - cx;
      const ax = p3.x - p0.x - cx - bx;

      const cy = 3 * (p1.y - p0.y);
      const by = 3 * (p2.y - p1.y) - cy;
      const ay = p3.y - p0.y - cy - by;

      const x = ax * Math.pow(t, 3) + bx * Math.pow(t, 2) + cx * t + p0.x;
      const y = ay * Math.pow(t, 3) + by * Math.pow(t, 2) + cy * t + p0.y;

      return { x, y };
    };

    const drawPlane = (plane: Plane) => {
      ctx.save();
      ctx.translate(plane.x, plane.y);
      ctx.rotate(plane.rotation);
      ctx.scale(plane.scale, plane.scale);

      // Draw trail
      if (plane.trail.length > 1) {
        ctx.beginPath();
        ctx.moveTo(plane.trail[0].x, plane.trail[0].y);
        
        for (let i = 1; i < plane.trail.length; i++) {
          const point = plane.trail[i];
          const prevPoint = plane.trail[i - 1];
          const xc = (point.x + prevPoint.x) / 2;
          const yc = (point.y + prevPoint.y) / 2;
          ctx.quadraticCurveTo(prevPoint.x, prevPoint.y, xc, yc);
        }

        const gradient = ctx.createLinearGradient(
          plane.trail[0].x, plane.trail[0].y,
          plane.trail[plane.trail.length - 1].x, plane.trail[plane.trail.length - 1].y
        );
        gradient.addColorStop(0, `rgba(152, 245, 225, 0)`);
        gradient.addColorStop(0.5, `rgba(152, 245, 225, ${plane.opacity * 0.3})`);
        gradient.addColorStop(1, `rgba(152, 245, 225, 0)`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      // Draw paper plane
      ctx.fillStyle = `rgba(152, 245, 225, ${plane.opacity})`;
      ctx.beginPath();
      ctx.moveTo(-15, 0);
      ctx.lineTo(15, 0);
      ctx.lineTo(0, 10);
      ctx.closePath();
      ctx.fill();

      // Wing details
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.lineTo(-8, 0);
      ctx.strokeStyle = `rgba(152, 245, 225, ${plane.opacity * 0.5})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.restore();
    };

    const updatePlane = (plane: Plane) => {
      // Update path progress
      plane.pathProgress += plane.speed;
      if (plane.pathProgress >= 1) {
        Object.assign(plane, createPlane());
        return;
      }

      // Calculate current position on the path
      const t = plane.pathProgress;
      const currentPoint = getBezierPoint(plane.path, t);
      const nextPoint = getBezierPoint(plane.path, Math.min(1, t + 0.01));

      // Update position
      plane.x = currentPoint.x;
      plane.y = currentPoint.y;

      // Calculate rotation based on movement direction
      plane.rotation = Math.atan2(nextPoint.y - currentPoint.y, nextPoint.x - currentPoint.x);

      // Update trail
      plane.trail.unshift({ x: plane.x, y: plane.y });
      if (plane.trail.length > plane.trailLength) {
        plane.trail.pop();
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      planes.forEach(plane => {
        updatePlane(plane);
        drawPlane(plane);
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    initPlanes();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0 pointer-events-none"
      style={{ background: 'transparent' }}
    />
  );
};

export default PaperPlanes;